import { z } from 'zod';
import { TenantProductSchema } from '../products';
import type { VoiceChannelCounterState } from '../schemas/voice-channel-usage';

// Tenant base schema
export const TenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(100),
  domain: z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]$/),
  sip_domain: z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/),
  status: z.enum(['active', 'suspended', 'pending']),
  // Task #3148/#3264 — prodotto/edition (`tenants.product`), SoT catalogo:
  // PRODUCT_REGISTRY in packages/shared/src/products.ts.
  product: TenantProductSchema.default('easyflow'),
  created_at: z.date(),
  updated_at: z.date(),
  settings: z.object({
    max_concurrent_calls: z.number().min(1).max(1000).default(20),
    max_users: z.number().min(1).max(10000).default(50),
    max_ai_agents: z.number().min(1).max(1000).default(10),
    recording_enabled: z.boolean().default(true),
    gdpr_compliant: z.boolean().default(true),
    timezone: z.string().default('Europe/Rome'),
    language: z.string().default('it')
  })
});

export type Tenant = z.infer<typeof TenantSchema>;

// Store schema
export const StoreSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  name: z.string().min(1).max(100),
  // Task #1802: derivato in lettura da `store_brands.retail_code` (brand primary).
  // Nullable se la sede non ha un brand primary con retail_code valorizzato.
  store_id: z.string().min(1).max(64).nullable(),
  status: z.enum(['active', 'inactive']),
  created_at: z.date(),
  updated_at: z.date(),
  settings: z.object({
    business_hours: z.object({
      enabled: z.boolean().default(true),
      timezone: z.string().default('Europe/Rome'),
      schedule: z.record(z.object({
        open: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/),
        close: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)
      }))
    }),
    outbound_caller_id: z.string().optional(),
    recording_consent_required: z.boolean().default(true)
  })
});

export type Store = z.infer<typeof StoreSchema>;

/** Proiezione per i selettori; stessa paginazione del GET /stores. */
export const StoreOptionSchema = StoreSchema.pick({ id: true, name: true }).extend({
  status: z.enum(['active', 'inactive', 'suspended', 'archived']),
});
export type StoreOption = z.infer<typeof StoreOptionSchema>;
export const StoreOptionsPageSchema = z.object({
  items: z.array(StoreOptionSchema),
  pagination: z.object({
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});
export type StoreOptionsPage = z.infer<typeof StoreOptionsPageSchema>;

// ---------------------------------------------------------------------------
// Cancellazione sede guidata (tenant).
// GET /stores/:id/delete-preview → StoreDeletePreview
// DELETE /stores/:id rifiutata (409, code STORE_DELETE_BLOCKED) con
// `details` = StoreDeletePreview quando la sede ha magazzini collegati
// (FK SET NULL lascerebbe il magazzino senza sede) o utenti il cui recinto
// dipende SOLO da quella sede (FK CASCADE → modalità `none`, non vedono più
// nulla). Entrambe le condizioni vanno risolte riassegnando PRIMA.
// ---------------------------------------------------------------------------
export const STORE_DELETE_BLOCKED_CODE = 'STORE_DELETE_BLOCKED';

export const StoreDeletePreviewWarehouseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type StoreDeletePreviewWarehouse = z.infer<typeof StoreDeletePreviewWarehouseSchema>;

export const StoreDeletePreviewUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  full_name: z.string(),
});
export type StoreDeletePreviewUser = z.infer<typeof StoreDeletePreviewUserSchema>;

export const StoreDeletePreviewSchema = z.object({
  store_id: z.string().uuid(),
  /** Magazzini con `pos_warehouses.store_id` = sede: vanno spostati su un'altra sede. */
  warehouses: z.array(StoreDeletePreviewWarehouseSchema),
  /** Utenti il cui recinto cadrebbe a `none`: va assegnato un altro recinto. */
  users: z.array(StoreDeletePreviewUserSchema),
  /** Utenti con una riga di recinto sulla sede (anche con altri ambiti): cache da invalidare. */
  scoped_user_count: z.number().int().nonnegative(),
  can_delete: z.boolean(),
});
export type StoreDeletePreview = z.infer<typeof StoreDeletePreviewSchema>;

// Extension/Internal schema
export const ExtensionSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  store_id: z.string().uuid().optional(),
  extension: z.string().regex(/^[0-9]{3,6}$/),
  password: z.string().min(8).max(32),
  display_name: z.string().min(1).max(100),
  status: z.enum(['active', 'inactive', 'locked']),
  type: z.enum(['user', 'queue', 'conference', 'voicemail']),
  created_at: z.date(),
  updated_at: z.date(),
  settings: z.object({
    voicemail_enabled: z.boolean().default(true),
    call_forwarding: z.object({
      enabled: z.boolean().default(false),
      destination: z.string().optional()
    }),
    dnd_enabled: z.boolean().default(false),
    recording_enabled: z.boolean().default(true)
  })
});

export type Extension = z.infer<typeof ExtensionSchema>;


// Default limits applied when settings JSONB is missing fields (no migration required).
export const TENANT_LIMITS_DEFAULTS = {
  max_concurrent_calls: 20,
  max_users: 50,
  max_ai_agents: 10,
} as const;

// Update payload for master limits endpoint (all fields optional → partial update).
export const TenantLimitsUpdateSchema = z.object({
  max_concurrent_calls: z.number().int().min(1).max(1000).optional(),
  max_users: z.number().int().min(1).max(10000).optional(),
  max_ai_agents: z.number().int().min(1).max(1000).optional(),
});
export type TenantLimitsUpdate = z.infer<typeof TenantLimitsUpdateSchema>;

export type TenantLimitStatus = 'ok' | 'warning' | 'full';

export interface TenantLimitSection {
  current: number;
  limit: number;
  status: TenantLimitStatus;
}

export interface TenantLimitsResponse {
  tenant_id: string;
  voip: TenantLimitSection & { counter: VoiceChannelCounterState };
  users: TenantLimitSection;
  ai_agents: TenantLimitSection;
}

export function computeLimitStatus(current: number, limit: number): TenantLimitStatus {
  if (limit <= 0) return 'full';
  if (current >= limit) return 'full';
  if (current / limit >= 0.8) return 'warning';
  return 'ok';
}
